import type { Investment, SpeakerKit, SpeakingProfile } from '../types';
import { config } from '../config';

/** Render an Investment value for display. */
export function formatInvestment(inv: Investment): string {
  if (inv.mode === 'upon-request') return 'Investment: available upon request.';
  const fmt = (n: number) => `$${n.toLocaleString('en-US')}`;
  return `Investment: ${fmt(inv.low)}–${fmt(inv.high)}${inv.note ? ` (${inv.note})` : ''}`;
}

export const SPEAKING: SpeakingProfile = {
  positioning: 'I work with professionals who respond to domestic abuse and want to see the pattern, not just the incident.',
  headline: 'Three keynotes. One lane: coercive control and pattern-based abuse.',
  investment: { mode: 'upon-request' },
  talks: [
    {
      id: 'incident',
      title: 'The Danger of the "Incident": Seeing the Pattern, Seeing the Risk',
      hook: 'Incident-based frameworks capture what happened on one night. They miss the pattern of power and control that signals lethality.',
      takeaways: [
        'Recognize coercive control where incident-based frameworks see "no visible injury"',
        'Ask the intake and interview questions that surface the pattern',
        'Document in language that captures risk and holds up in court',
        'Assess lethality from the pattern, not the last event',
      ],
      audience: 'Law Enforcement · Prosecutors · Healthcare · Advocates',
      formats: ['Keynote', 'Workshop', 'Virtual'],
      duration: '60-min keynote · 90-min breakout · half-day workshop',
    },
    {
      id: 'after-she-leaves',
      title: 'After She Leaves: Post-Separation Abuse and the Systems That Miss It',
      hook: 'For survivors with children, separation is often when the danger escalates — and family court becomes the new arena for control.',
      takeaways: [
        'Identify how abuse continues through custody, finances, and litigation',
        'Distinguish high-conflict divorce from coercive use of the court',
        'Recognize when court and child-welfare decisions extend harm to children',
        'Plan for safety when ongoing contact cannot be avoided',
      ],
      audience: 'Family Law · Judges · GALs · Mediators · Child Welfare',
      formats: ['Keynote', 'Workshop', 'Virtual'],
      duration: '60-min keynote · 90-min CLE session · half-day institute',
    },
    {
      id: 'documenting',
      title: 'Documenting the Pattern: From Incident Reports to Accountability',
      hook: "When a patrol report, an ER note, and a caseworker's file each tell part of the story, escalation stays invisible.",
      takeaways: [
        'Build timelines that reveal escalation',
        'Shift report, record, and petition language without overstating evidence',
        'Coordinate documentation across law enforcement, healthcare, courts, and advocates',
        'Connect pattern documentation to survivor safety outcomes',
      ],
      audience: 'All Professional Audiences · Multidisciplinary Teams',
      formats: ['Keynote', 'Workshop', 'Virtual'],
      duration: '60-min keynote · 3–4 hour hands-on workshop',
    },
  ],
};

export const SPEAKER_KIT: SpeakerKit = {
  get oneSheetUrl() { return config.oneSheetUrl; },
  bioShort:
    'Tracy Curtis, M.A., CDVP, is a domestic abuse and coercive control subject-matter expert, speaker, and consultant. Founder of EmpowerED: From Awareness to Accountability, she has developed state-recognized programs reaching more than 3,000 law enforcement, legal, healthcare, and community professionals. Her work is rooted in professional expertise and lived experience.',
  bioLong:
    'Tracy Curtis, M.A., CDVP, changes how professionals respond to domestic abuse. She works with law enforcement, legal, healthcare, child-welfare, and community practitioners who want to move from awareness to accountability — from documenting isolated incidents to recognizing, documenting, and responding to abuse as a pattern of power and control.\n\n' +
    'As founder of EmpowerED: From Awareness to Accountability, LLC, Tracy has developed state-recognized programs that have reached more than 3,000 professionals across disciplines. Her programs address coercive control, post-separation abuse, technology-facilitated abuse, non-fatal strangulation, and the impact of abuse on children, and are built around the questions professionals actually ask on scene, in the exam room, and in the courtroom.\n\n' +
    'Tracy holds a Master of Arts in Forensic Psychology from The Chicago School of Professional Psychology and is a Certified Domestic Violence Professional (CDVP), with certifications in Mental Health First Aid and Applied Suicide Intervention Skills Training (ASIST). Before founding EmpowerED she served as a domestic violence education coordinator and as a family-preservation case coordinator working with families involved in the child-welfare system.\n\n' +
    'Her work is grounded in both professional expertise and lived experience. As she puts it: "When we call it an \'incident,\' we miss the pattern. When we miss the pattern, we miss the risk." Tracy is based in Indiana and speaks nationally, in person and virtually.',
  introScript:
    "Every professional in this room has responded to a call, a case, or a patient that didn't add up — and walked away uneasy, not sure why. That unease has a name: coercive control.\n\n" +
    'When we treat domestic abuse as a series of isolated incidents, we miss the pattern. And when we miss the pattern, we miss the risk.\n\n' +
    'Our speaker has spent her career working with the exact professionals sitting in this room — law enforcement, attorneys, healthcare providers, advocates — helping them see what the paperwork obscures and act on what the evidence reveals. She is the founder of EmpowerED: From Awareness to Accountability, holds a Master\'s in Forensic Psychology, and is a Certified Domestic Violence Professional. Her programs have reached more than 3,000 professionals.\n\n' +
    "Today you'll leave with a framework for moving beyond incident response into pattern recognition — and the accountability tools to back it up.\n\n" +
    'Please welcome Tracy Curtis.',
  links: [
    { label: 'LinkedIn', href: 'https://www.linkedin.com/in/tracycurtismacdvp' },
    { label: 'Watch the keynote excerpt', href: 'https://www.youtube.com/watch?v=Hu9r8h3CczI' },
    { label: 'curtisempowered@gmail.com', href: 'mailto:curtisempowered@gmail.com' },
  ],
};
